"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import type { User, Session } from "@supabase/supabase-js";
import { reportJsError } from "@/lib/js-error-beacon";
import { clearAccountSession, getAccountSessionSnapshot } from "@/lib/account-session";

import {
  needsSupabaseSession,
  onSupabaseClientStarted,
  supabaseSessionStorageKey,
} from "@/lib/supabase-session-activation";

type AuthRuntime = typeof import("./AuthProvider.runtime");

let authRuntimePromise: Promise<AuthRuntime> | null = null;

function loadAuthRuntime(): Promise<AuthRuntime> {
  authRuntimePromise ??= import("./AuthProvider.runtime").catch((error: unknown) => {
    authRuntimePromise = null;
    throw error;
  });
  return authRuntimePromise;
}

interface VedicUser {
  credits: number;
  moon_rashi: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  vedicUser: VedicUser | null;
  loading: boolean;
  signInWithPhone: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, token: string) => Promise<User | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** @internal */
export async function fetchVedicUser(userId: string): Promise<VedicUser | null> {
  const { getSupabase } = await loadAuthRuntime();
  const { data, error } = await getSupabase()
    .from("vedic_users")
    .select("credits, moon_rashi")
    .eq("id", userId)
    .single();
  if (error) {
    // PGRST116 = "no rows" — normal for a session whose vedic_users row isn't
    // provisioned yet; stay quiet. ANY other error (a schema/contract drift like
    // the 2026-06-25 dropped-`subscription_tier` 400, an RLS misconfig, a transport
    // failure) is a real defect that must not be silently swallowed — feed the owned
    // js_error beacon (events_raw, free, rule 34 / rule-60 bug-layer) so the failure
    // class is queryable next time instead of only flooding the browser console.
    if (error.code !== "PGRST116") {
      reportJsError({
        error_message: `vedic_users fetch failed: ${error.message}`,
        error_name: `supabase_${error.code || "unknown"}`,
        boundary: "AuthProvider.fetchVedicUser",
      });
    }
    return null;
  }
  if (!data) return null;
  return data as VedicUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [vedicUser, setVedicUser] = useState<VedicUser | null>(null);
  const [loading, setLoading] = useState(true);
  const activateRuntime = useRef<(() => Promise<AuthRuntime>) | null>(null);
  const ensureRuntime = useCallback(() => activateRuntime.current?.() ?? loadAuthRuntime(), []);

  // Load vedic user data when auth user changes
  const loadVedicUser = useCallback(async (u: User | null) => {
    if (!u) {
      setVedicUser(null);
      return;
    }
    const vu = await fetchVedicUser(u.id);
    setVedicUser(vu);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    let activation: Promise<AuthRuntime> | null = null;
    const start = (): Promise<AuthRuntime> => {
      activation ??= loadAuthRuntime()
        .then((runtime) => {
          if (cancelled) return runtime;
          unsubscribe = runtime.observeAuthSession({
            onSession: (s) => {
              setSession(s);
              setUser(s?.user ?? null);
              void loadVedicUser(s?.user ?? null);
            },
            onReady: () => setLoading(false),
            onSignedOut: () => {
              if (getAccountSessionSnapshot()?.source === "auth") clearAccountSession();
            },
          });
          return runtime;
        })
        .catch((error: unknown) => {
          activation = null;
          if (!cancelled) setLoading(false);
          throw error;
        });
      return activation;
    };
    activateRuntime.current = start;
    const request = () => {
      void start().catch(() => {});
    };
    const stopWatching = onSupabaseClientStarted(request);
    const storageChanged = (event: StorageEvent) => {
      if (event.newValue === null) return;
      try {
        if (event.key === supabaseSessionStorageKey()) request();
      } catch {
        request();
      }
    };
    window.addEventListener("storage", storageChanged);
    if (needsSupabaseSession()) request();
    else
      queueMicrotask(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (activateRuntime.current === start) activateRuntime.current = null;
      stopWatching();
      window.removeEventListener("storage", storageChanged);
      unsubscribe?.();
    };
  }, [loadVedicUser]);

  const signInWithPhone = useCallback(
    async (phone: string) => {
      const { authSignIn } = await ensureRuntime();
      await authSignIn(phone);
    },
    [ensureRuntime]
  );

  const verifyOtp = useCallback(
    async (phone: string, token: string) => {
      const { authVerifyOtp } = await ensureRuntime();
      const data = await authVerifyOtp(phone, token);
      // Session will be picked up by onAuthStateChange
      return data.user;
    },
    [ensureRuntime]
  );

  const signOut = useCallback(async () => {
    const { authSignOut, resetUser } = await ensureRuntime();
    await authSignOut();
    clearAccountSession();
    setUser(null);
    setSession(null);
    setVedicUser(null);
    resetUser();
  }, [ensureRuntime]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        vedicUser,
        loading,
        signInWithPhone,
        verifyOtp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
